import React, { forwardRef } from 'react';
import { APITypes } from '../../../../../../primary-record-types';
import ChatEntry from './ChatEntry';
import Text from '../../../../../v2BaseComponents/Text';
import { Animated, StyleProp, View, ViewStyle } from 'react-native';
import { getSpacing } from '../../../../../../utils/spacing';
import Stack from '../../../../../v2BaseComponents/Stack';
import { PlaceholderMessage } from './utils';
import ChatReferences from './ChatReferences';
import { colors } from '../../../../../../utils/theme';

export type ChatMessageMessage = APITypes.chatService.Message | PlaceholderMessage;

export type ChatMessageOwnProps = {
    message: ChatMessageMessage;
};

type Role = 'model' | 'user';
type RoleWithProcessing = Role | 'processing';

const ChatEntryContainer = forwardRef<
    View,
    {
        children: React.ReactNode;
        role: RoleWithProcessing;
        style?: StyleProp<ViewStyle>;
    }
>((props, ref) => {
    const userOrModelRole = props.role === 'user' ? 'user' : 'model';

    return (
        <Stack
            horizontal
            style={[
                {
                    justifyContent: userOrModelRole === 'model' ? 'flex-start' : 'flex-end',
                    width: '100%',
                    marginTop: getSpacing(4),
                },
                props.style,
            ]}
            testID={`chat-message-${props.role}`}
            ref={ref}
        >
            <ChatEntry role={userOrModelRole}>{props.children}</ChatEntry>
        </Stack>
    );
});

export type ChatMessageProps = ChatMessageOwnProps;

export const ChatMessage = forwardRef<View, ChatMessageProps>(({ message }, ref) => {
    // Handle model messages with status
    if (message.role === 'model') {
        if ('status' in message && message.status === 'processing') {
            // Render the processing placeholder inline (dots animation)
            return <ModelProcessingPlaceholder />;
        }
        if ('status' in message && message.status === 'failed') {
            return (
                <ChatEntryContainer role="model" ref={ref}>
                    <Text variant="h6" style={{ color: colors.Alert }}>
                        Failed to generate response
                    </Text>
                </ChatEntryContainer>
            );
        }
        // Complete model message (status is 'complete' or undefined)
        const completeMessage = message as APITypes.chatService.ModelMessageComplete;
        return (
            <ChatEntryContainer role={message.role} ref={ref}>
                <Text variant="h6">{completeMessage.text}</Text>
                <ChatReferences message={completeMessage} />
            </ChatEntryContainer>
        );
    }

    // User messages
    return (
        <ChatEntryContainer role={message.role} ref={ref}>
            <Text variant="h6">{(message as APITypes.chatService.UserMessage).text}</Text>
        </ChatEntryContainer>
    );
});

export const ModelProcessingPlaceholder: React.FC = () => {
    const dot1 = React.useRef(new Animated.Value(0)).current;
    const dot2 = React.useRef(new Animated.Value(0)).current;
    const dot3 = React.useRef(new Animated.Value(0)).current;

    const totalDelay = 600;

    React.useEffect(() => {
        const animate = (dot: Animated.Value, delay: number) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(dot, {
                        toValue: -8,
                        useNativeDriver: false,
                        duration: 400,
                    }),
                    Animated.timing(dot, {
                        toValue: 0,
                        useNativeDriver: false,
                        duration: 400,
                    }),
                    Animated.delay(totalDelay - delay),
                ]),
            );

        const animations = [animate(dot1, 0), animate(dot2, 200), animate(dot3, 400)];

        Animated.parallel(animations).start();
    }, [dot1, dot2, dot3]);

    return (
        <ChatEntryContainer role={'processing'}>
            <Stack
                horizontal
                style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    paddingBottom: getSpacing(4),
                }}
                testID="model-processing-placeholder"
            >
                <Animated.View style={{ transform: [{ translateY: dot1 }] }}>
                    <Text variant="model-processing-dot">.</Text>
                </Animated.View>
                <Animated.View style={{ transform: [{ translateY: dot2 }] }}>
                    <Text variant="model-processing-dot">.</Text>
                </Animated.View>
                <Animated.View style={{ transform: [{ translateY: dot3 }] }}>
                    <Text variant="model-processing-dot">.</Text>
                </Animated.View>
            </Stack>
        </ChatEntryContainer>
    );
};
